import { useCallback, useEffect, useMemo, useState } from "react"
import axios from 'axios'
import pluralize from 'pluralize'
import { useSnackbar } from 'notistack'
import { useSelector, useDispatch } from 'react-redux'
import { message } from 'antd'
import { useLocation } from "react-router-dom"
import { io } from "socket.io-client"

import { logout } from "./store/actions"

import Pagination from "./components/resusable/pagination"

import beep from './sounds/beep.mp3'

// export const host = localStorage.getItem('host')

// export const port = localStorage.getItem('port')

export const production = process.env.REACT_APP_ENV === 'production'

export const server = process.env.REACT_APP_API_URL || 'http://localhost:5400'

export const ws = io(server)

export const useToggle = (initialState = false) => {
    const [state, setState] = useState(initialState)

    const toggle = useCallback(() => setState(state => !state), [])

    return [state, toggle, setState]
}

export const useQuery = () => {
    const { search } = useLocation()
  
    return useMemo(() => new URLSearchParams(search), [search])
}

let registered = []

export const useSocketEvents = events => {
    const [item] = useState(0)

    const { enqueueSnackbar, closeSnackbar } = useSnackbar()
    const { user } = useSelector(state => state.application)
    // const [play] = useSound(beep)
    const audio = new Audio(beep)

    useEffect(() => {
        if (user)
            for (let e in events) {
                if (!registered.includes(e)) {
                    ws.on(e, data => {
                        let { callback, notif, condition = () => true } = events[e]
        
                        if (condition(data)) {
                            if (notif) {
                                audio.play()
                                enqueueSnackbar(notif?.message ?? data.message, notifOps(notif?.type ?? data?.type ?? 'success' ))
                            }
                            console.log(callback)
                            callback(data)
                        }
                    })
                    registered.push(e)
                }
            }
    }, [item])
}

export const plural = (unit, count) => pluralize(unit, count)

export const notifOps = (type, action = null, persist = false, duration = 3000) => ({
    variant: type,
    persist,
    autoHideDuration: persist ? null : duration,
    action
})

export const stringifyDate = (date, noTime = false) => {
    let d = new Date(date).toString()
    d = d.slice(0, d.indexOf(' GMT'))

    if (noTime)
        d = d.slice(0, d.lastIndexOf(' '))

    return d
}

export const normalize = (val, nearest = 100) => (Math.round(val * nearest) / nearest)

export const usePet = (endpoint = '', payload, { notify, callback = () => null, manual, load, hasFile, method = 'post', logOutOnError = false }) => {
    load = load ? {
        msg: load.msg || 'loading'
    } : null

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [data, setData] = useState(null)
    const [lock, setLock] = useState(manual ? true : false)

    const { enqueueSnackbar, closeSnackbar } = useSnackbar()

    const dispatch = useDispatch()

    const kickOut = () => dispatch(logout())

    const errorNotif = msg => enqueueSnackbar(msg, notifOps('warning', () => (
        <button
            className="close-notif"
            onClick={ () => closeSnackbar() }>
            Close
        </button>
    ), true))

    const request = async () => {
        const form = hasFile ? new FormData() : {}

        for (let item in payload) {
            if (hasFile) {
                if (Array.isArray(payload[item])) {
                    if (payload[item].length > 0 && payload[item][0]?.type) {
                        payload[item].forEach(file => form.append(item, file))
                    } else {
                        form.append(item, JSON.stringify(payload[item]))
                    }
                } else {
                    if (payload[item])
                        form.append(item, payload[item])
                }
            } else {
                form[item] = payload[item]
            }
        }

        if (hasFile) {
            form.append('hasFile', true)
            form.append('timeStamp', new Date())
        } else {
            form.timeStamp = new Date()
        }

        try {
            setLoading(true)
            setError(false)
            let key
            if (manual && load)
                key = message.loading(load.msg)
            // key = enqueueSnackbar(loadingMsg, notifOps('info', loader, true))

            let token = sessionStorage.getItem('token')

            let response = await axios({
                url: `${server}/api/${endpoint}`,
                method,
                data: form,
                headers: token && { Authorization: `Bearer ${token}` }
            })

            setLoading(false)

            if (manual && load)
                key()
            // closeSnackbar(key)

            let { data: received } = response

            setData(received.data)
            callback(received, enqueueSnackbar, notifOps, closeSnackbar)
        } catch (err) {
            setLoading(false)
            setError(err)
            log({ err })

            if (notify) {
                if (err.response) {
                    if (err.response.data) {
                        if (err.response.data.message) {
                            errorNotif(err.response.data.message)
                        } else {
                            errorNotif(err.message)
                        }
                    } else {
                        errorNotif('An error occurred at our servers, please contact the admin.')
                    }
                } else if (err.request) {
                    errorNotif('We seem to be offline at the moment, please contact the admin.')
                } else {
                    errorNotif('You seem to be offline, please check your network connection')
                }
            }

            if (err.response?.data?.kickOut || logOutOnError)
                kickOut()
        }
    }

    const makeRequest = () => {
        if (!lock) {
            setLock(true)
            request()
        }
    }

    useEffect(makeRequest)

    const execute = () => setLock(false)

    if (!manual)
        makeRequest()

    return [{ loading, error, data }, execute]
}

export const log = item => production ? null : console.log(item)

export const useListData = (query, payload, options, name, lim = 10, callback = () => null) => {
    const [list, setList] = useState([])
    const [count, setCount] = useState(0)
    const [page, setPage] = useState(1)
    const [limit] = useState(lim)

    const [{ loading, error, data }, fetch] = usePet(query, {
        ...payload,
        page,
        limit
    }, {
        ...options,
        callback: ({ data }) => {
            setList(data[name ?? query])
            setCount(data.count)
            ws.send('engaged-patients')
            callback(data)
        }
    })

    const navigate = pg => {
        setPage(pg)
        fetch()
    }

    const Paginate = <Pagination total={count} limit={limit} page={page} navigate={navigate} />

    return [{ loading, error, fetch }, { list, count, data }, { navigate, page, limit, Paginate }]
}

export const getGradient = key => {
    let backgrounds = ['grad1', 'grad2', 'grad3', 'grad4']

    let mult = 4
    
    while (true) {
        if (key < mult)
            return backgrounds[(mult - 1) - key]
            
        mult += 4
    }
}
